import { apiFetch } from './http';

export interface TerrainView {
  id: number;
  /** The world class: the identifier, shown only where it is one (the catalogue's "classe" column). */
  worldName: string;
  /** Always a readable name: the scan's, a curated one, or one derived from the class (WorldNames). */
  displayName: string;
  /** Metres per side; null or 0 when the scan did not find it. */
  worldSize: number | null;
  source: string;
  modWorkshopId: number | null;
  /** The mod's own name, from its meta.cpp or mod.cpp; null for a vanilla world. */
  modName?: string | null;
}

/** A named place of a world, as the probe read it out of CfgWorlds >> Names. */
export interface PlaceView {
  world: string;
  name: string;
  /** The engine's kind: NameCityCapital, NameCity, NameVillage, NameLocal, Hill, Airport... */
  type: string;
  x: number;
  y: number;
  radius: number;
  /** True for the kinds a mission can be built around; the platform decides which. */
  settlement?: boolean;
}

export interface ScanView {
  id: number;
  modSetHash: string;
  /** The server whose mods were scanned and what it was called then; a label, the catalogue is the mod set's. */
  serverId: number | null;
  serverName: string | null;
  status: 'STARTING' | 'RUNNING' | 'DONE' | 'FAILED';
  chunksReceived: number;
  chunksExpected: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface CatalogueSummary {
  modSetHash: string;
  worlds: number;
  factions: number;
  groups: number;
  vehicles: number;
  weapons: number;
  magazines: number;
  engine: string;
  worldList: { className: string; displayName: string; worldSize: number }[];
  factionList: { className: string; displayName: string; side: number }[];
}

/**
 * Which mod defined a faction, a unit, a vehicle or an item (roadmap item
 * mod-d-origen): `game` the base game and the DLCs that load with it, `cdlc` a
 * Creator DLC, `mod` a mod; `unknown` when no addon of the catalogue claims the
 * class, `unread` when the catalogue was scanned before the probe read it.
 * `name` is null for the base game itself and for a mod known only by its
 * Workshop id. Absent from a platform older than the field.
 */
export interface ModOrigin {
  kind: 'game' | 'cdlc' | 'mod' | 'unknown' | 'unread';
  name: string | null;
  workshopId: number | null;
}

/** The list view: the profile's JSON stays on the server until an editor asks for one profile. */
export interface FactionProfileView {
  id: number;
  modSetHash: string;
  name: string;
  factionClass: string;
  side: string;
  derived: boolean;
  /**
   * The faction's own flag, as a texture path in the game's files. Empty for a
   * faction that declares none and for a catalogue scanned before the probe
   * learnt to read one; the pickers then draw the emblem they always drew.
   */
  picture?: string;
  /**
   * Whether the catalogue has infantry groups of this faction. A faction of
   * vehicles only cannot be the enemy of a night (nothing to spawn on foot);
   * absent on a catalogue the server could not read, and then not judged.
   */
  infantry?: boolean;
  /**
   * The group preset a hand-made faction plays as (its JSON's `squadPresetId`):
   * picking it as our own forces fills the slot table with that squad, its
   * classes and its saved loadouts. Absent or null for every other faction.
   */
  squadPresetId?: number | null;
  /**
   * Whether the faction is irregular — guerrillas, insurgents, a militia —
   * rather than an army (release D, D5.5): an editor's mark, else read from its
   * names. Absent from a platform older than the field.
   */
  irregular?: boolean;
  /** `hand` when an editor marked it, `catalogue` when it is read from its names. */
  irregularSource?: 'hand' | 'catalogue';
  /**
   * Whose cars its men ride when a mounted force is asked and it has no mounted
   * group of its own (2026-10-10): `own` from its own config faction, `groups`
   * from the config faction its own groups name, `donor` from the faction an
   * editor declared (`ridesFrom`), `none` when its mounted forces go on foot;
   * `unknown` on a catalogue the server could not read, absent on an older platform.
   */
  rides?: 'own' | 'groups' | 'donor' | 'none' | 'unknown';
  /** The declared ride donor's config faction, or null. */
  ridesFrom?: string | null;
  /** The config faction the rides come from, whatever the source, or null. */
  ridesFaction?: string | null;
  /**
   * A hand-made enemy faction's own men, rides and guns, counted
   * (docs/design/HANDMADE_ENEMY_FACTIONS.md); null when it has none.
   */
  roster?: { men: number; vehicles: number; statics: number; loadouts: number } | null;
  /** The mod that defined the faction's config class. */
  origin?: ModOrigin;
}

export interface FactionProfileDetail extends FactionProfileView {
  profileJson: string;
}

/** The roles a roster's man may have, in the order the editor offers them. */
export const ROSTER_ROLES = [
  'leader',
  'rifleman',
  'autorifleman',
  'machinegunner',
  'grenadier',
  'at',
  'aa',
  'marksman',
  'sniper',
  'medic',
  'engineer',
  'uav',
  'crew',
  'officer',
] as const;

/** The kinds a roster's static answers, as the runtime asks for them. */
export const ROSTER_STATIC_KINDS = ['hmg', 'gmg', 'at', 'aa', 'mortar'] as const;

export interface RosterMan {
  unitClass: string;
  role: string;
  label?: string;
  loadoutId?: string;
  weight: number;
}

/** A hand-made enemy faction's roster, as the profile's JSON holds it. */
export interface FactionRoster {
  men: RosterMan[];
  vehicles: string[];
  statics: { className: string; kind: string }[];
}

/** The roster a profile's JSON carries, or an empty one. */
export function rosterOf(profileJson: string | undefined | null): FactionRoster {
  try {
    const r = JSON.parse(profileJson ?? '{}')?.roster;
    if (!r || typeof r !== 'object') return { men: [], vehicles: [], statics: [] };
    return {
      men: Array.isArray(r.men)
        ? r.men.map((m: Record<string, unknown>) => ({
            unitClass: String(m.unitClass ?? ''),
            role: String(m.role ?? 'rifleman'),
            label: m.label ? String(m.label) : undefined,
            loadoutId: m.loadoutId != null && m.loadoutId !== '' ? String(m.loadoutId) : undefined,
            weight: Number(m.weight) || 1,
          }))
        : [],
      vehicles: Array.isArray(r.vehicles) ? r.vehicles.map(String) : [],
      statics: Array.isArray(r.statics)
        ? r.statics.map((s: Record<string, unknown>) => ({
            className: String(s.className ?? ''),
            kind: String(s.kind ?? 'hmg'),
          }))
        : [],
    };
  } catch {
    return { men: [], vehicles: [], statics: [] };
  }
}

/** Sets a hand-made profile's roster, or clears it (null); the platform checks it against the mod set's catalogue. */
export const setFactionRoster = (
  id: number,
  roster: FactionRoster | null,
  modSetHash: string | null,
) =>
  apiFetch<FactionProfileDetail>(`/api/catalog/factions/${id}/roster`, {
    method: 'PUT',
    body: { roster, modSetHash },
  });

/** A hand-made copy of a profile under a new name. */
export const cloneFactionProfile = (id: number, name: string) =>
  apiFetch<FactionProfileDetail>(`/api/catalog/factions/${id}/clone`, {
    method: 'POST',
    body: { name },
  });

export interface LoadoutView {
  id: number;
  name: string;
  role: string | null;
  side: string | null;
  source: string;
  modSetHash: string | null;
  loadoutJson: string;
  /**
   * The mods its weapons, wearables, items and backpack come from, the mods
   * first and the base game last; one `unread` on a catalogue scanned before
   * the probe read them, empty with no catalogue to read it against.
   */
  origins?: ModOrigin[];
}

export interface LoadoutValidation {
  loadout: { primary: { className: string } | null; uniform: { className: string } | null };
  missing: string[];
  catalogueKnown: boolean;
}

export interface PanelMod {
  id: number;
  name: string;
  installationStatus: string | null;
  serverOnly: boolean;
  /** Bytes on the panel's disk; null when the panel has not measured it. */
  fileSize?: number | null;
}

/** A size as a person reads it: '1,2 GB', '340 MB'; empty when unknown. */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || n <= 0) return '';
  if (n >= 1e9) return `${(n / 1e9).toLocaleString('ca', { maximumFractionDigits: 1 })} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  return `${Math.max(1, Math.round(n / 1e3))} kB`;
}

/**
 * What a server runs and whether the platform has read it. The mods are the
 * server's own: the panel's presets are Launcher files imported once, and the
 * platform neither reads nor writes them (2026-09-19).
 */
export interface Readiness {
  /** The identity of the server's Workshop mods and Creator DLCs. */
  modSetHash: string;
  /** Whether a catalogue of that mod set exists; without one no mission can be made for the server. */
  scanned: boolean;
  /** A scan of it is running now. */
  scanning?: boolean;
  /**
   * Whether that scan read which mod each faction, unit and item came from. A
   * scan made before v0.89.0 did not: every origin of the mod set says "not
   * read" until the server is scanned again. Absent from an older platform.
   */
  modOrigins?: boolean;
  /** How many Workshop mods it runs, and its Creator DLCs by name. */
  mods?: number;
  dlcs?: string[];
  /** The mods a player has to carry: everything the panel does not mark server-only. */
  clientMods?: string[];
  /**
   * Which fire-support system this mod set plays with: 'simplex' when it carries
   * Simplex Support Services, 'vanilla' for the game's own menu. It changes what
   * a support is: the game's own 0-8 menu or Simplex's, both held back by a
   * cooldown between calls and neither counted (the owner, 2026-09-26), so the
   * supports card asks for a tick and says which menu the night will use.
   */
  supportsSystem?: 'simplex' | 'vanilla';
}

/** The terrains what a server runs can host, or every terrain the panel has when no server is named. */
export const fetchTerrains = (serverId?: number) =>
  apiFetch<TerrainView[]>(`/api/catalog/terrains${serverId ? `?serverId=${serverId}` : ''}`);
export const scanTerrains = () =>
  apiFetch<{ pbosRead: number; terrainsFound: number; errors: string[] }>(
    '/api/catalog/terrains/scan',
    {
      method: 'POST',
    },
  );
/** One terrain's survey, without the height grid: what the catalogue's chip shows. */
/**
 * The kinds of ground each terrain of a mod set carries (`airfield`, `site`,
 * `settlement`, `high_ground`, `water`…), keyed by the world's class in lower
 * case, so the map step can say which terrains cannot host the type chosen.
 */
export async function fetchTerrainKinds(hash: string): Promise<Record<string, string[]>> {
  return apiFetch<Record<string, string[]>>(
    `/api/catalog/${encodeURIComponent(hash)}/terrains/kinds`,
  );
}

export interface TerrainProfileView {
  worldName: string;
  status: 'QUEUED' | 'STARTING' | 'RUNNING' | 'DONE' | 'FAILED';
  error: string | null;
  chunksReceived: number;
  chunksExpected: number | null;
  worldSize: number | null;
  gridStep: number | null;
  /** The height grid's side in nodes: set once a survey has completed, and kept while the next one runs. */
  cols?: number | null;
  engine: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  bridges: number;
  highGround: number;
  flatAreas: number;
  coast: number;
  /** Shores gentle enough to land on, inland water, and how many separate land masses the map has. */
  beaches: number;
  lakes: number;
  landMasses: number;
  /** Cells of the survey's vegetation lattice with trees in them; 0 on a survey run before the pass existed. */
  vegetation?: number;
  /** The survey's version (null before 2026-09-27, which is 1), the version the platform writes now, and whether this map should be surveyed again. */
  surveyVersion?: number | null;
  currentSurveyVersion?: number;
  needsResurvey?: boolean;
  /** Whether the survey is kept as it arrived (so its facts can be made again with no server run), and the derivation the facts were made with against today's. */
  rawSurvey?: boolean;
  derivedVersion?: number | null;
  currentDerivationVersion?: number;
  /** Survey v2's own counts: 0 on a version-1 survey. */
  buildings?: number;
  buildingClasses?: number;
  unclassifiedClasses?: number;
  junctions?: number;
  bridgeRecords?: number;
  transmitters?: number;
  runways?: number;
}

/** How much of a mod set's ground has been looked at, and what is still waiting. */
export interface SurveyStatus {
  total: number;
  done: number;
  queued: number;
  running: string | null;
  /** Whether the queue is going one Arma server at a time, which is what surveying every map asks for. */
  sequential: boolean;
  /** Whether an admin has stopped the queue from starting its rows; a running survey finishes. */
  paused: boolean;
  pending: TerrainView[];
}

export const fetchTerrainProfiles = () =>
  apiFetch<TerrainProfileView[]>('/api/catalog/terrains/profiles');
export const fetchSurveyStatus = (serverId?: number) =>
  apiFetch<SurveyStatus>(
    `/api/catalog/terrains/survey-status${serverId ? `?serverId=${serverId}` : ''}`,
  );
/** Surveys one terrain: one Arma server, a few minutes, and a profile at the end. */
export const surveyTerrain = (world: string, modSetHash: string | null, force = false) =>
  apiFetch<TerrainProfileView>(`/api/catalog/terrains/${encodeURIComponent(world)}/survey`, {
    method: 'POST',
    body: { modSetHash, force },
  });
/**
 * Queues every terrain that has never been surveyed: a server's, or every map
 * the panel has when no server is named. `skipped` names the modded terrains no
 * scanned catalogue knows — the platform will not start a server for a mission
 * whose terrain addon it would have to guess.
 */
export const surveyAllTerrains = (serverId: number | null, force = false, sequential = false) =>
  apiFetch<{ queued: number; worlds: string[]; skipped: string[]; sequential: boolean }>(
    '/api/catalog/terrains/survey-all',
    { method: 'POST', body: { serverId, force, sequential } },
  );

/** Pauses or resumes the survey queue (admin). */
export const pauseSurveys = (paused: boolean) =>
  apiFetch<{ paused: boolean }>('/api/catalog/terrains/survey-pause', {
    method: 'POST',
    body: { paused },
  });

/** One terrain's map: whether the Atlas has it, and whether this host has downloaded it. */
export interface TerrainMapState {
  worldName: string;
  displayName: string;
  available: boolean;
  local: boolean;
  downloading: boolean;
  tiles: number;
  bytes: number;
}

export const fetchTerrainMaps = (serverId?: number) =>
  apiFetch<TerrainMapState[]>(
    `/api/catalog/terrains/maps${serverId ? `?serverId=${serverId}` : ''}`,
  );
/** Downloads one terrain's whole map onto this host: one request to the Atlas, then never again. */
export const downloadTerrainMap = (world: string, force = false) =>
  apiFetch<TerrainMapState>(`/api/catalog/terrains/${encodeURIComponent(world)}/map/download`, {
    method: 'POST',
    body: { force },
  });
export const downloadTerrainMaps = (serverId: number | null, force = false) =>
  apiFetch<{ queued: number; worlds: string[] }>('/api/catalog/terrains/maps/download', {
    method: 'POST',
    body: { serverId, force },
  });

/** Every scan, newest first; `perServer` keeps only each server's newest (the admin's catalog tab). */
export const fetchScans = (perServer = false) =>
  apiFetch<ScanView[]>(perServer ? '/api/catalog/scans?perServer=true' : '/api/catalog/scans');
/** Scans what a server runs now, on a throwaway server of the platform's own; the server itself is never touched. */
export const startScan = (serverId: number, force = false) =>
  apiFetch<ScanView>('/api/catalog/scans', { method: 'POST', body: { serverId, force } });
export const fetchSummary = (hash: string) =>
  apiFetch<CatalogueSummary>(`/api/catalog/${hash}/summary`);
/** The towns and landmarks of one world, settlements first (`preferredPlace`). */
export const fetchPlaces = (hash: string, world: string) =>
  apiFetch<PlaceView[]>(`/api/catalog/${hash}/places?world=${encodeURIComponent(world)}`);
export const fetchFactionProfiles = (hash: string) =>
  apiFetch<FactionProfileView[]>(`/api/catalog/${hash}/factions`);
/**
 * A texture out of the game's own files, as PNG.
 *
 * A faction's flag, a weapon's icon, a vehicle's preview: all of them are `.paa`
 * inside the PBOs the panel already has, and nothing of anybody else's art is
 * shipped with this platform — it serves what the host it runs beside owns.
 * A 404 is the ordinary answer for a path nothing can read, and every caller
 * falls back to what it drew before.
 */
export const textureUrl = (path: string) => `/api/catalog/texture?path=${encodeURIComponent(path)}`;

/**
 * A class's own picture, addressed by class rather than by path.
 *
 * The panel answers this one: it has been extracting weapon icons and vehicle
 * previews out of the same installation since August, caching them on the same
 * disk, and refusing the dedicated server's stripped 8x8 placeholders — which
 * decode perfectly well and would otherwise be published as grey squares. The
 * platform's own readers are the fallback, using the texture path the probe
 * recorded, which is why `hash` is worth sending when it is known.
 */
export const pictureUrl = (
  kind: 'weapon' | 'vehicle' | 'marker',
  className: string,
  hash?: string,
) =>
  `/api/catalog/picture?kind=${kind}&class=${encodeURIComponent(className)}` +
  (hash ? `&hash=${encodeURIComponent(hash)}` : '');

/**
 * Which factions would show their real flag and which would not.
 *
 * A dedicated server's base-game textures are 8x8 placeholders, so every vanilla
 * faction's flag is one: those can only come from a copy taken out of a client
 * installation and dropped into the platform's art directory, named `seedAs`.
 * Mod factions fill themselves from their own PBOs.
 */
export interface ArtCoverage {
  factionClass: string;
  displayName: string;
  side: string;
  hasFlag: boolean;
  /** The file to drop in: the texture's own last segment. */
  seedAs: string;
}

export const fetchArtCoverage = (hash: string) =>
  apiFetch<ArtCoverage[]>(`/api/catalog/${hash}/art-coverage`);

export const fetchFactionProfile = (id: number) =>
  apiFetch<FactionProfileDetail>(`/api/catalog/factions/${id}`);

/** One CfgGroups entry: a squad, a fireteam, a vehicle section, as the game ships it. */
export interface GroupAsset {
  className: string;
  displayName: string;
  category: string;
  units: number;
  vehicles: string[];
}

export interface UnitAsset {
  className: string;
  displayName: string;
  /** Its own picture in the game's files, when it declares one. */
  picture?: string;
  /** The mod that defined the class. */
  origin?: ModOrigin;
}

export interface VehicleAsset {
  className: string;
  displayName: string;
  /** man · car · truck-ish (`land`) · apc · tank · heli · plane · ship · static · other. */
  kind: string;
  transportSoldier: number;
  /** The editor's own preview of it, when it has one. */
  picture?: string;
  /** The mod that defined the class. */
  origin?: ModOrigin;
}

/** Everything a faction has, as the Eden editor's own asset browser lists it. */
export interface FactionAssets {
  modSetHash: string;
  faction: string;
  displayName: string;
  side: string;
  groups: GroupAsset[];
  units: UnitAsset[];
  vehicles: VehicleAsset[];
}

/**
 * A faction's groups, men and vehicles. 404 while the preset has no scanned
 * catalogue, which is a normal state: the screens that use it fall back to
 * letting somebody name a faction without naming its classes.
 */
export const fetchFactionAssets = (hash: string, faction: string) =>
  apiFetch<FactionAssets>(`/api/catalog/${hash}/assets?faction=${encodeURIComponent(faction)}`);

/**
 * One vehicle of the whole catalogue, whatever faction fields it.
 *
 * The faction's own list ({@link FactionAssets}) answers "what has this faction
 * got"; this answers "what is in this mod set", which is the question a squad's
 * starting vehicles actually ask.
 */
export interface VehicleHit {
  className: string;
  displayName: string;
  /** car · truck-ish (`land`) · apc · tank · heli · plane · ship · static. */
  kind: string;
  transportSoldier: number;
  picture?: string;
  factionClass?: string;
  /** The faction's own display name, or its class when the catalogue has no name for it. */
  factionName?: string;
  side?: string;
  /** The mod that defined the class. */
  origin?: ModOrigin;
}

/** A page of the vehicle catalogue, with how many each kind holds under the same filter. */
export interface VehiclePage {
  modSetHash: string;
  /** How many the filter matches, against however many this page carries. */
  total: number;
  /** Count per kind, under the faction and the search but not under the kind. */
  kinds: Record<string, number>;
  vehicles: VehicleHit[];
}

export interface VehicleQuery {
  faction?: string | null;
  kind?: string | null;
  q?: string | null;
  offset?: number;
  /** 0 asks for the counts alone, which is what a browser needs before a category is picked. */
  limit?: number;
}

/**
 * The vehicle catalogue, browsed. Empty while the preset has no scanned
 * catalogue, the same normal state every other catalogue reader falls back from.
 */
export const searchVehicles = (hash: string, query: VehicleQuery = {}) => {
  const params = new URLSearchParams();
  if (query.faction) params.set('faction', query.faction);
  if (query.kind) params.set('kind', query.kind);
  if (query.q) params.set('q', query.q);
  if (query.offset) params.set('offset', String(query.offset));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return apiFetch<VehiclePage>(`/api/catalog/${hash}/vehicles?${params.toString()}`);
};

/** The ones already picked, resolved back to their names and kinds. */
export const fetchVehiclesByClass = (hash: string, classes: string[]) => {
  const params = new URLSearchParams();
  classes.forEach((c) => params.append('classes', c));
  return apiFetch<VehiclePage>(`/api/catalog/${hash}/vehicles?${params.toString()}`);
};

/** A mod set's base model: the composition a land or a sea start is built from. */
export interface BaseModelView {
  /** land · sea. */
  kind: string;
  name: string;
  presetId: number;
}

export const fetchBaseModels = (hash: string, era?: string | null) =>
  apiFetch<BaseModelView[]>(
    `/api/catalog/${hash}/base-models${era ? `?era=${encodeURIComponent(era)}` : ''}`,
  );

/**
 * The vehicle a means proposes for our faction: its own, else its family's on
 * its side, else none — never a helicopter from another era (`Transports.java`).
 */
export const fetchTransport = (
  hash: string,
  query: { means: string; faction?: string | null; side?: string | null; seats?: number },
) => {
  const params = new URLSearchParams({ means: query.means });
  if (query.faction) params.set('faction', query.faction);
  if (query.side) params.set('side', query.side);
  if (query.seats) params.set('seats', String(query.seats));
  return apiFetch<{ means: string; vehicle: VehicleHit | null }>(
    `/api/catalog/${hash}/transport?${params.toString()}`,
  );
};

/**
 * What a faction has to travel in and to be supported by
 * (`FactionPieces.java`): a way in is offered only when there is something to
 * travel in, a support only when there is a piece for it, and a drone only in a
 * modern night. Null when nobody has read the mod set: everything is then
 * offered, as it was.
 */
export interface FactionPieces {
  /** helicopter, boat, vehicle; and parachute, which needs an aircraft and a runway on the terrain. */
  means: Record<string, boolean>;
  /** artillery, cas, transport, supplyDrop, uav, medevac. */
  supports: Record<string, boolean>;
  /** Why a support answered false, by key: `era`, `no_carrier`, `no_piece`. Empty when no server's system was sent. */
  supportReasons?: Record<string, string>;
  /** Whether there is a transport aircraft to jump from. */
  plane: boolean;
}

export const fetchFactionPieces = (
  hash: string,
  query: {
    faction?: string | null;
    side?: string | null;
    era?: string | null;
    seats?: number;
    /** The terrain, for the one means that needs something of it: a runway to take off from. */
    world?: string | null;
    /** What the server plays its supports with (`simplex` or `vanilla`): narrows the supports to what it can carry. */
    system?: string | null;
  },
) => {
  const params = new URLSearchParams();
  if (query.world) params.set('world', query.world);
  if (query.system) params.set('system', query.system);
  if (query.faction) params.set('faction', query.faction);
  if (query.side) params.set('side', query.side);
  if (query.era) params.set('era', query.era);
  if (query.seats) params.set('seats', String(query.seats));
  return apiFetch<FactionPieces | null>(`/api/catalog/${hash}/pieces?${params.toString()}`);
};

// --- unit and group presets ---------------------------------------------------

/** One man of a group preset, or the whole of a unit preset. */
export interface ForcePresetMember {
  /** A unit preset followed into, instead of naming a class here. */
  unitPresetId?: number | null;
  unitClass?: string | null;
  loadoutId?: string | null;
  role?: string | null;
  label?: string | null;
  count?: number | null;
}

/** The body of a unit preset, of a group preset, and of a composition. */
export interface ForcePresetBody {
  /** Compositions: the objects, each measured from the composition's own middle. */
  objects?: { className: string; offset: number[]; azimuth?: number; simple?: boolean }[];
  /** How far it reaches from that middle. */
  radius?: number;
  /** Where it was read from: 'objectsMapper' or 'missionSqm'. */
  source?: string;
  /** Unit presets: the man class this preset is. */
  unitClass?: string | null;
  loadoutId?: string | null;
  role?: string | null;
  /** Group presets: what it is made of, and what it comes with. */
  category?: string | null;
  members?: ForcePresetMember[];
  vehicles?: { className: string; count?: number }[];
  /**
   * Group presets a faction plays as: who flies its helicopter, when it has
   * one. Not a member — a pilot is not one per squad — but the class and the
   * saved loadout the generator gives the pilot and the copilot it adds.
   */
  aircrew?: { unitClass?: string | null; loadoutId?: string | null } | null;
}

export interface ForcePresetView {
  id: number;
  modSetHash: string;
  kind: 'UNIT' | 'GROUP' | 'COMPOSITION';
  name: string;
  side: string;
  factionClass: string | null;
  bodyJson: string;
  /** Classes this mod set has not got: a preset built against another one. */
  missing: string[];
  updatedAt: string;
}

export interface ForcePresetInput {
  kind?: 'unit' | 'group' | 'composition';
  name: string;
  side: string;
  factionClass?: string | null;
  bodyJson: string;
}

export const fetchForcePresets = (hash: string, kind?: 'unit' | 'group' | 'composition') =>
  apiFetch<ForcePresetView[]>(`/api/catalog/${hash}/force-presets${kind ? `?kind=${kind}` : ''}`);
export const createForcePreset = (hash: string, input: ForcePresetInput) =>
  apiFetch<ForcePresetView>(`/api/catalog/${hash}/force-presets`, {
    method: 'POST',
    body: input,
  });
export const updateForcePreset = (id: number, input: ForcePresetInput) =>
  apiFetch<ForcePresetView>(`/api/catalog/force-presets/${id}`, { method: 'PUT', body: input });
export const cloneForcePreset = (id: number) =>
  apiFetch<ForcePresetView>(`/api/catalog/force-presets/${id}/clone`, { method: 'POST' });
export const deleteForcePreset = (id: number) =>
  apiFetch<null>(`/api/catalog/force-presets/${id}`, { method: 'DELETE' });

// --- the composition library (docs/design/COMPOSITION_LIBRARY.md) -------------

/** An era, and the theatre within it when the tag names one. */
export interface LibraryTag {
  era: string;
  theatre: string | null;
}

/** One composition as a mod set sees it (`CompositionLibrary.Entry`). */
export interface LibraryEntry {
  id: number;
  key: string;
  name: string;
  /** OWNER, AGENT, WORKSHOP, MISSION or PLATFORM. */
  origin: string;
  storage: 'REPOSITORY' | 'DATABASE';
  /** `start_base:camp`, `start_base:site`, `start_base:sea`, `fob`, `scene:<recipe>:<slot>`. */
  roles: string[];
  tags: LibraryTag[];
  footprint: number;
  objects: number;
  approved: boolean;
  approvedAt: string | null;
  enabled: boolean;
  disabledReason: string | null;
  author: string | null;
  licence: string | null;
  workshopId: string | null;
  /** The eras this mod set can build it in. */
  eras: string[];
  /** For a composition no mod set can build: the classes missing. */
  missing: string[];
  /** The bench's pictures of the version it has now, the view from above first. */
  pictures: LibraryPicture[];
}

/** One bench picture: TOP, VIEW_A, VIEW_B, VIEW_C or DETAIL, on a terrain. */
export interface LibraryPicture {
  id: number;
  view: string;
  world: string;
}

/** Where a picture is served from. */
export const libraryPictureUrl = (compositionId: number, pictureId: number) =>
  `/api/compositions/${compositionId}/pictures/${pictureId}`;

/**
 * One of the bench's views, uploaded by an admin (multipart, so it goes round
 * `apiFetch` as the composition upload does). The platform's refusal code
 * comes back in the error's message.
 */
export async function uploadLibraryPicture(
  compositionId: number,
  file: File,
  view: string,
  world: string,
): Promise<LibraryPicture> {
  const form = new FormData();
  form.append('file', file);
  form.append('view', view);
  form.append('world', world);
  const token = document.cookie
    .split('; ')
    .find((r) => r.startsWith('XSRF-TOKEN='))
    ?.slice('XSRF-TOKEN='.length);
  const response = await fetch(`/api/compositions/${compositionId}/pictures`, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {},
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `http_${response.status}`);
  }
  return (await response.json()) as LibraryPicture;
}

/** Takes one picture away (admin). */
export const removeLibraryPicture = (compositionId: number, pictureId: number) =>
  apiFetch<null>(`/api/compositions/${compositionId}/pictures/${pictureId}`, { method: 'DELETE' });

export interface LibraryView {
  scanned: boolean;
  compositions: LibraryEntry[];
}

/** One composition's page: its entry, its classes and its objects for the sketch. */
export interface LibraryDetail {
  entry: LibraryEntry;
  classes: string[];
  objects: { className: string; offset: number[] }[];
  /** The functional anchors of an imported composition, by name (`park` is a list). */
  anchors?: Record<string, unknown> | null;
  /** The Workshop item it was imported from: id, link and, when Steam answered, title, preview and update date. */
  workshop?: {
    id: string;
    link: string;
    title?: string;
    previewUrl?: string;
    updated?: string;
  } | null;
  /** What the import left out and counted. */
  imported?: {
    read: number;
    kept: number;
    dropped: Record<string, number>;
  } | null;
}

/** Every composition this mod set can build whole. */
export const fetchLibrary = (hash: string) =>
  apiFetch<LibraryView>(`/api/catalog/${encodeURIComponent(hash)}/library`);
export const fetchLibraryDetail = (id: number, hash: string | null) =>
  apiFetch<LibraryDetail>(
    `/api/compositions/${id}${hash ? `?hash=${encodeURIComponent(hash)}` : ''}`,
  );
/** The compositions no scanned mod set can build (admin). */
export const fetchLibraryIncompatible = () =>
  apiFetch<LibraryEntry[]>('/api/compositions?compatible=none');
/** An admin's switch; on is refused (`not_approved`) until the owner approved the version. */
export const setLibraryEnabled = (
  id: number,
  enabled: boolean,
  reason: string | null,
  hash: string | null,
) =>
  apiFetch<LibraryEntry>(
    `/api/compositions/${id}/enabled${hash ? `?hash=${encodeURIComponent(hash)}` : ''}`,
    { method: 'PUT', body: { enabled, reason } },
  );
/** The owner approves the version a composition has now. */
export const approveLibraryComposition = (id: number, hash: string | null) =>
  apiFetch<LibraryEntry>(
    `/api/compositions/${id}/approve${hash ? `?hash=${encodeURIComponent(hash)}` : ''}`,
    { method: 'POST' },
  );

// --- the Workshop import (docs/design/COMPOSITION_LIBRARY.md §5, §10) ----------

/** Why a file was not read: `over_cap` carries its piece count and the limit (500). */
export interface ImportRefusal {
  code: string;
  count: number;
  limit: number;
}

/** One functional anchor, in the kit's frame: the middle at 0,0 and the gate facing east (+x). */
export interface ImportAnchor {
  /** `spawn`, `flag`, `orders`, `arsenal`, `medic`, `gate`, `park` (numbered by `index`) or `helipad`. */
  name: string;
  index: number;
  x: number;
  y: number;
  azimuth: number;
  /** What found it: `catalogue`, `name`, `class`, `gap`, `open ground`, `you` or `to place`. */
  how: string;
  /** The piece it stands on (an index into the body's objects), or -1. */
  piece: number;
  /** False for an anchor with nothing to propose, drawn at the middle for the person to drag. */
  placed: boolean;
}

export interface ImportDropped {
  men: number;
  vehicles: number;
  staticWeapons: number;
  roads: number;
  leftOut: number;
  modules: number;
  triggers: number;
  markers: number;
  waypoints: number;
  inits: number;
  vehicleClasses: string[];
  staticClasses: string[];
}

export interface ImportCheck {
  key: string;
  n: number;
  detail: string;
}

export interface ImportReport {
  form: string;
  read: number;
  kept: number;
  dropped: ImportDropped;
  scanned: boolean;
  missing: string[];
  leftOutMissing: string[];
  guessedHeights: { className: string; height: number }[];
  origins: {
    mod: string;
    kind: string;
    eraHint: string | null;
    pieces: number;
    examples: string[];
  }[];
  factions: { faction: string; pieces: number; examples: string[] }[];
  lamps: number;
  unmeasured: number;
  unmeasuredExamples: string[];
  front: number;
  footprint: number;
  checks: ImportCheck[];
}

export interface ImportWorkshop {
  id: string | null;
  title: string | null;
  creatorId: string | null;
  previewUrl: string | null;
  updated: string | null;
  link: string | null;
  /** `bad_link` or `steam_unavailable` when there is nothing more to show. */
  error: string | null;
}

/** The review form's data. */
export interface ImportPreview {
  refusal: ImportRefusal | null;
  fileName: string | null;
  suggestedName: string;
  body: {
    objects: { className: string; offset: number[]; azimuth?: number; role?: string }[];
    footprint: number;
  } | null;
  report: ImportReport | null;
  anchors: ImportAnchor[];
  workshop: ImportWorkshop | null;
}

/** What the form confirmed. */
export interface ImportMeta {
  name: string;
  roles: string[];
  tags: { era: string; theatre: string | null }[];
  author: string;
  licence: string;
  link: string | null;
  withoutMissing: boolean;
  anchors: { name: string; index: number; x: number; y: number; azimuth: number }[];
}

/** The platform refused an import: `code` is for the form, `count` and `limit` for the cap. */
export class ImportError extends Error {
  constructor(
    public readonly code: string,
    public readonly count: number,
    public readonly limit: number,
  ) {
    super(code);
  }
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const token = document.cookie
    .split('; ')
    .find((r) => r.startsWith('XSRF-TOKEN='))
    ?.slice('XSRF-TOKEN='.length);
  const response = await fetch(path, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {},
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      count?: number;
      limit?: number;
    } | null;
    throw new ImportError(
      body?.error ?? `http_${response.status}`,
      body?.count ?? 0,
      body?.limit ?? 0,
    );
  }
  return (await response.json()) as T;
}

/** Reads an uploaded composition.sqe or mission.sqm and the Workshop link, and keeps nothing (admin). */
export function previewImport(
  hash: string | null,
  file: File,
  link: string,
  withoutMissing: boolean,
): Promise<ImportPreview> {
  const form = new FormData();
  form.append('file', file);
  if (hash) form.append('hash', hash);
  if (link.trim()) form.append('link', link.trim());
  form.append('withoutMissing', String(withoutMissing));
  return postForm<ImportPreview>('/api/compositions/import/preview', form);
}

/** Keeps the reviewed import as a library row, disabled and unapproved (admin). */
export function saveImport(
  hash: string | null,
  file: File,
  meta: ImportMeta,
): Promise<LibraryEntry> {
  const form = new FormData();
  form.append('file', file);
  if (hash) form.append('hash', hash);
  form.append('meta', JSON.stringify(meta));
  return postForm<LibraryEntry>('/api/compositions/import', form);
}

// --- compositions: where they come from ---------------------------------------

/** One composition found somewhere, ready to be saved under a name. */
export interface CompositionCandidate {
  name: string;
  /** 'objectsMapper' or 'missionSqm': which of the two forms it was read as. */
  form: string;
  objects: number;
  radius: number;
  /** Classes this mod set has not got. */
  missing: string[];
  /** The preset body, ready for the ordinary save endpoint. */
  body: string;
}

export const fetchCompositionSources = () =>
  apiFetch<{ missions: string[]; workshop: string[] }>('/api/catalog/compositions/sources');
export const compositionsFromWorkshop = (hash: string, workshopId: string) =>
  apiFetch<CompositionCandidate[]>(
    `/api/catalog/${hash}/compositions/from-workshop?workshopId=${encodeURIComponent(workshopId)}`,
  );
export const compositionsFromMission = (hash: string, folder: string, anchor?: string) =>
  apiFetch<CompositionCandidate[]>(
    `/api/catalog/${hash}/compositions/from-mission?folder=${encodeURIComponent(folder)}${
      anchor ? `&anchor=${encodeURIComponent(anchor)}` : ''
    }`,
  );
export const readComposition = (hash: string, name: string, text: string) =>
  apiFetch<CompositionCandidate>(`/api/catalog/${hash}/compositions/read`, {
    method: 'POST',
    body: { name, text },
  });

/**
 * A file dropped on the page: a framework's composition, exported from Eden and
 * saved somewhere that is not the panel's own volume. Multipart, so it goes
 * round `apiFetch` the way the preset import already does.
 */
export async function uploadComposition(hash: string, file: File): Promise<CompositionCandidate> {
  const form = new FormData();
  form.append('file', file);
  const token = document.cookie
    .split('; ')
    .find((r) => r.startsWith('XSRF-TOKEN='))
    ?.slice('XSRF-TOKEN='.length);
  const response = await fetch(`/api/catalog/${hash}/compositions/upload`, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {},
  });
  if (!response.ok) throw new Error(`upload failed: ${response.status}`);
  return (await response.json()) as CompositionCandidate;
}
/**
 * Mark a faction irregular or regular by hand, or give the answer back to the
 * catalogue's names with `null` (release D, D5.5).
 */
export const markFactionIrregular = (id: number, irregular: boolean | null) =>
  apiFetch<FactionProfileView>(`/api/catalog/factions/${id}/irregular`, {
    method: 'PUT',
    body: { irregular },
  });
/** An editor declares whose cars a faction's men ride when it owns none, or clears it (null). */
export const setFactionRidesFrom = (id: number, ridesFrom: string | null) =>
  apiFetch<FactionProfileView>(`/api/catalog/factions/${id}/rides-from`, {
    method: 'PUT',
    body: { ridesFrom },
  });
export const deriveFactionProfiles = (hash: string) =>
  apiFetch<FactionProfileView[]>(`/api/catalog/${hash}/factions/derive`, { method: 'POST' });
export const validateLoadout = (sqf: string, modSetHash: string | null) =>
  apiFetch<LoadoutValidation>('/api/catalog/loadouts/validate', {
    method: 'POST',
    body: { sqf, modSetHash },
  });
export interface LoadoutSources {
  missions: string[];
  sessions: { code: string; missionTitle: string; endedAt: string | null; players: number }[];
}

export interface LoadoutCandidate {
  name: string;
  sqf: string;
  validation: LoadoutValidation;
}

export const fetchLoadoutSources = () => apiFetch<LoadoutSources>('/api/catalog/loadouts/sources');
export const importLoadouts = (
  source: { mission: string } | { sessionCode: string },
  modSetHash: string | null,
) =>
  apiFetch<LoadoutCandidate[]>('/api/catalog/loadouts/import', {
    method: 'POST',
    body: { ...source, modSetHash },
  });
export const saveLoadout = (input: {
  name: string;
  role?: string;
  side?: string;
  sqf: string;
  modSetHash: string | null;
}) => apiFetch<LoadoutView>('/api/catalog/loadouts', { method: 'POST', body: input });
export const fetchLoadouts = (hash?: string) =>
  apiFetch<LoadoutView[]>(`/api/catalog/loadouts${hash ? `?modSetHash=${hash}` : ''}`);

/** A weapon of a kit as the platform stores it (`Loadout.Weapon`): attachments by class, at most two magazines. */
export interface KitWeapon {
  className: string;
  muzzle: string | null;
  laser: string | null;
  optic: string | null;
  magazines: { className: string; count: number }[];
  bipod: string | null;
}

/** A uniform, vest or backpack and what is in it. The class may be empty while an imported script's contents are not. */
export interface KitContainer {
  className: string;
  contents: { className: string; count: number }[];
}

/** A loadout in the stored shape (`Loadout`): what the editor edits and what validation reads. */
export interface LoadoutKit {
  primary: KitWeapon | null;
  launcher: KitWeapon | null;
  handgun: KitWeapon | null;
  uniform: KitContainer | null;
  vest: KitContainer | null;
  backpack: KitContainer | null;
  headgear: string | null;
  goggles: string | null;
  binocular: string | null;
  linkedItems: string[];
}

export const emptyKit = (): LoadoutKit => ({
  primary: null,
  launcher: null,
  handgun: null,
  uniform: null,
  vest: null,
  backpack: null,
  headgear: null,
  goggles: null,
  binocular: null,
  linkedItems: [],
});

/** The kit of a stored loadout, or an empty one when its JSON does not read. */
export function kitOf(loadoutJson: string | null | undefined): LoadoutKit {
  try {
    const o = JSON.parse(loadoutJson ?? '') as Partial<LoadoutKit>;
    return { ...emptyKit(), ...o, linkedItems: Array.isArray(o.linkedItems) ? o.linkedItems : [] };
  } catch {
    return emptyKit();
  }
}

/** What a kit can hold, each a class of the mod set; the picker's filter chips. */
export const GEAR_KINDS = [
  'uniform',
  'vest',
  'headgear',
  'backpack',
  'goggles',
  'primary',
  'launcher',
  'handgun',
  'binocular',
  'optic',
  'muzzle',
  'laser',
  'bipod',
  'item',
  'magazine',
] as const;
export type GearKind = (typeof GEAR_KINDS)[number];

export interface GearHit {
  className: string;
  displayName: string;
  kind: string;
  origin: ModOrigin | null;
}

export interface GearPage {
  modSetHash: string;
  total: number;
  offset: number;
  limit: number;
  /** How many rows each kind has under the same word and mod, the kind filter itself aside. */
  kinds: Record<string, number>;
  items: GearHit[];
}

/** One page of the mod set's wearables, weapons, items and magazines: the loadout editor never lists them all. */
export const searchGear = (
  hash: string,
  query: {
    kind?: string | null;
    q?: string | null;
    mod?: string | null;
    /** Only these class names: how a kit that is opened gets the names of what it holds. */
    classes?: string[];
    offset?: number;
    limit?: number;
  } = {},
) => {
  const params = new URLSearchParams();
  if (query.kind) params.set('kind', query.kind);
  if (query.q) params.set('q', query.q);
  if (query.mod) params.set('mod', query.mod);
  query.classes?.forEach((c) => params.append('classes', c));
  if (query.offset) params.set('offset', String(query.offset));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return apiFetch<GearPage>(`/api/catalog/${hash}/gear?${params.toString()}`);
};

export interface LoadoutProblem {
  /** `uniform`, `primary`, `primary.magazine`, `vest.contents`, `kit`, `modSet`... */
  slot: string;
  className: string;
  /** not_in_mod_set · wrong_kind · bad_count · too_many_magazines · empty · no_catalogue */
  reason: string;
  /** What the catalogue says the class is, for `wrong_kind`. */
  found: string;
}

export interface LoadoutCheck {
  catalogueKnown: boolean;
  problems: LoadoutProblem[];
}

export const fetchLoadout = (id: number) => apiFetch<LoadoutView>(`/api/catalog/loadouts/${id}`);

/** A kit read against its mod set without saving it. */
export const checkLoadout = (kit: LoadoutKit, modSetHash: string | null) =>
  apiFetch<LoadoutCheck>('/api/catalog/loadouts/check', {
    method: 'POST',
    body: { loadout: kit, modSetHash },
  });

/**
 * Saves a kit built in the editor: a new one, or the one `id` names. The platform checks it like an
 * imported kit and answers 422 with the slot and the reason of each fault, which `problemsOf` reads.
 */
export const saveLoadoutKit = (input: {
  id?: number;
  name: string;
  role?: string | null;
  side?: string | null;
  kit: LoadoutKit;
  modSetHash: string | null;
}) => {
  const body = {
    name: input.name,
    role: input.role ?? null,
    side: input.side ?? null,
    loadout: input.kit,
    modSetHash: input.modSetHash,
  };
  return input.id === undefined
    ? apiFetch<LoadoutView>('/api/catalog/loadouts', { method: 'POST', body })
    : apiFetch<LoadoutView>(`/api/catalog/loadouts/${input.id}`, { method: 'PUT', body });
};

/** The faults a refused save carries, or none for any other failure. */
export function problemsOf(e: unknown): LoadoutProblem[] {
  const body = (e as { body?: { problems?: unknown } } | null)?.body;
  return Array.isArray(body?.problems) ? (body.problems as LoadoutProblem[]) : [];
}
export const fetchMods = () => apiFetch<PanelMod[]>('/api/catalog/mods');
export const fetchReadiness = (serverId: number) =>
  apiFetch<Readiness>(`/api/catalog/servers/${serverId}/readiness`);

/** One runway or helipad the scan read from the world's own config; `heading` is the approach's azimuth. */
export interface AirfieldView {
  kind: string;
  index: number;
  x: number;
  y: number;
  heading: number;
}

/** What a terrain's config names, and the mod set it was read under (null when no scan carries the world). */
export interface NamedView {
  modSetHash: string | null;
  places: PlaceView[];
  airfields: AirfieldView[];
}

export const fetchNamed = (world: string, hash?: string | null) =>
  apiFetch<NamedView>(
    `/api/catalog/worlds/${encodeURIComponent(world)}/named${hash ? `?hash=${encodeURIComponent(hash)}` : ''}`,
  );

/** A cluster of military objects the survey found: what a force can be based at or sent against. */
export interface SurveySite {
  x: number;
  y: number;
  radius?: number;
  count?: number;
  kinds?: Record<string, number>;
}

/** The survey's located finds. Points are `[x, y]` in metres; older surveys lack the later passes. */
export interface SurveyFeatures {
  bridges?: number[][];
  highGround?: number[][];
  flatAreas?: number[][];
  beaches?: number[][];
  lakes?: number[][];
  landMasses?: unknown[];
  sites?: SurveySite[];
}

/** One located list of the survey: how many it keeps, and how many the terrain has when that is known. */
export interface SurveyListCount {
  shown: number;
  /** Null when the list is full and the survey did not count: unknown, never the cap. */
  total: number | null;
  capped: boolean;
}

/** A runway the survey measured: threshold, heading of the take-off run and length in metres. */
export interface SurveyRunway {
  x: number;
  y: number;
  heading: number;
  length: number;
  paved: boolean;
  /** `main`, `secondary`, `dynamic`, or empty on an older survey. */
  airport: string;
  index: number;
  name: string;
}

/** A bridge the survey measured: its span, whether water is under it and the drop from its deck. */
export interface SurveyBridge {
  x: number;
  y: number;
  span: number;
  overWater: boolean;
  drop: number;
}

/**
 * The figures of a terrain's page, computed by the platform from the last completed survey
 * (`TerrainSummary.java`). A null is a thing that survey did not measure: the page leaves it out.
 */
export interface TerrainSummary {
  landKm2: number | null;
  waterKm2: number | null;
  landPercent: number | null;
  highest: number | null;
  mean: number | null;
  landMasses: number | null;
  largestMasses: { x: number; y: number; km2: number }[];
  lists: Partial<Record<string, SurveyListCount>>;
  buildings: number | null;
  runways: SurveyRunway[];
  bridgeRecords: SurveyBridge[];
  transmitters: number | null;
  /** Metres, the lowest dry node; absent from a platform older than the field. */
  lowest?: number | null;
  /** Metres, the longest bridge with a measured span; null when none carries one. */
  longestBridge?: number | null;
  /** Places of the landmarks list (churches, factories, towers); null before survey v3. */
  landmarks?: number | null;
  /** Objects by type of building, most first, without loose props and untyped classes. */
  buildingTypes?: { type: string; objects: number; classes: number }[] | null;
  /** Survey v6's ground as figures; null before survey v6. */
  ground?: SurveyGroundSummary | null;
  /** The built-up cells the terrain's own ruins stand in; null before survey v2, absent from an older platform. */
  ruinPlaces?: number | null;
}

/** Objects counted per 100 m cell: how many, over how many hectares. */
export interface SurveySpread {
  pieces: number;
  hectares: number;
}

/** What survey v6 measured of the ground beyond the lists (`TerrainSummary.Ground`). */
export interface SurveyGroundSummary {
  /** Kilometres by the engine's road type (`MAIN ROAD`, `ROAD`, `TRACK`, `TRAIL`, `footpath`), most first. */
  roadKm: Record<string, number>;
  fords: number;
  deadEnds: number;
  lamps: number;
  helipads: number;
  walls: SurveySpread;
  fences: SurveySpread;
  rocks: SurveySpread;
  powerLines: SurveySpread;
  /** The ground under the dry samples by the engine's surface name; "" sums the rest. */
  surfaces: { kind: string; percent: number }[];
  shorePoints: number;
  landableShore: number;
  minor: Record<string, SurveySpread>;
}

/** One terrain's survey with what it found, for the page of that terrain. Never cached: a re-survey shows at once. */
export const fetchTerrainProfile = (world: string) =>
  apiFetch<
    TerrainProfileView & {
      sites?: number;
      features?: SurveyFeatures | null;
      summary?: TerrainSummary | null;
    }
  >(`/api/catalog/terrains/${encodeURIComponent(world)}/profile`, { cache: 'no-store' });

/**
 * Island or continent (release D, step D2.3): the accent a map's enemy and
 * locals speak with, where the answer came from and why. `source` is
 * `override` (an admin set it on the terrain page), `survey` (the survey's
 * shape: does the biggest land mass reach the map's edge) or `default` (no
 * survey: continental). `why` is the platform's own sentence, in English.
 */
export interface VoiceRegionRow {
  world: string;
  kind: 'island' | 'continental';
  source: 'override' | 'survey' | 'default';
  why: string;
  /** The admin's value (`ISLAND`/`CONTINENTAL`), or null when the survey decides. */
  override: string | null;
}

/** Every surveyed terrain's region, and the ones an admin set (editors may read it). */
export const fetchVoiceRegions = () =>
  apiFetch<VoiceRegionRow[]>('/api/catalog/terrains/voice-regions');

/** Sets a terrain's region (`ISLAND`/`CONTINENTAL`), or gives it back to the survey with null. Admins only. */
export const setVoiceRegion = (world: string, region: 'ISLAND' | 'CONTINENTAL' | null) =>
  apiFetch<VoiceRegionRow>(`/api/catalog/terrains/${encodeURIComponent(world)}/voice-region`, {
    method: 'PUT',
    body: { region },
  });
